import { z } from 'zod';

/**
 * WhatsApp Business Platform (Cloud API) types shared by the API and web app.
 * Checked against Meta's Cloud API documentation on 2026-10-28.
 */

export const WHATSAPP_TEXT_MAX = 4096;

/** Meta's template statuses. Only APPROVED templates can be sent. */
export type WhatsAppTemplateStatus = 'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED' | 'DISABLED' | 'IN_APPEAL' | string;
export type WhatsAppTemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' | string;

export interface WhatsAppTemplateDto {
  id: string;
  name: string;
  language: string;
  status: WhatsAppTemplateStatus;
  category: WhatsAppTemplateCategory;
  /** Header text (when the header is text), body text and footer, with {{1}} / {{name}} placeholders. */
  headerText: string | null;
  bodyText: string;
  footerText: string | null;
  /** Placeholder names in the body, in order ("1", "2" or named parameters). */
  bodyParams: string[];
  /** Placeholder names in a text header. */
  headerParams: string[];
  parameterFormat: 'positional' | 'named';
  /** Null when Knownlead can send it; otherwise why not (media header, button variables, …). */
  unsupportedReason: string | null;
}

export interface WhatsAppConnectionDto {
  wabaId: string;
  phoneNumberId: string;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  qualityRating: string | null;
  /** How the number was connected. */
  onboarding: 'access_token' | 'embedded_signup';
  /** Knownlead subscribed the app to this WhatsApp Business Account's webhooks. */
  webhookSubscribed: boolean;
  /** The number is registered for Cloud API (Embedded Signup numbers need registering). */
  registered: boolean | null;
  templates: WhatsAppTemplateDto[];
  templatesFetchedAt: string | null;
  templatesError: string | null;
  lastInboundAt: string | null;
  issues: string[];
}

export interface WhatsAppConsentDto {
  status: 'opted_in' | 'opted_out' | 'unknown';
  method: string | null;
  note: string | null;
  recordedAt: string | null;
  recordedBy: { id: string; name: string } | null;
  /** The customer opted out of marketing messages at WhatsApp (error 131050). */
  marketingOptOut: boolean;
}

/** WhatsApp-specific reply information for an inquiry (null for other channels). */
export interface WhatsAppReplyDto {
  connectionId: string;
  /** Approved, supported templates can be sent (window closed or not). `items` are the sendable ones. */
  templates: { available: boolean; reason: string | null; items: WhatsAppTemplateDto[] };
  consent: WhatsAppConsentDto;
  /** Shown next to every WhatsApp send: Meta may charge the business. */
  chargeNotice: string;
}

export interface WhatsAppEmbeddedSignupStartDto {
  appId: string;
  configId: string;
  graphApiVersion: string;
  state: string;
}

export const WHATSAPP_CONSENT_METHODS = ['customer_messaged_first', 'website_form', 'verbal', 'written', 'other'] as const;
export const WHATSAPP_CONSENT_METHOD_LABELS: Record<(typeof WHATSAPP_CONSENT_METHODS)[number], string> = {
  customer_messaged_first: 'Customer asked us on WhatsApp to keep them updated',
  website_form: 'Opt-in checkbox on a form',
  verbal: 'Verbal consent (phone or in person)',
  written: 'Written consent (email, signed form)',
  other: 'Other',
};

export const WHATSAPP_CHARGE_NOTICE =
  'Meta bills your WhatsApp Business Account directly: template messages per message, by category and country; replies inside the 24-hour window are free up to Meta’s monthly allowance per number. Knownlead does not charge or pass on these costs.';

const digits = z.string().trim().regex(/^\d{5,25}$/, 'Use the numeric id shown in Meta’s dashboard.');

/** Connect a number with a token issued for Knownlead's own Meta app (developer test number, internal numbers). */
export const whatsappConnectSchema = z.object({
  wabaId: digits,
  phoneNumberId: digits,
  accessToken: z.string().trim().min(20).max(2000),
});

export const whatsappEmbeddedSignupCompleteSchema = z.object({
  state: z.string().min(10).max(200),
  code: z.string().min(10).max(2000),
  wabaId: digits,
  phoneNumberId: digits,
});

export const whatsappRegisterSchema = z.object({
  pin: z.string().regex(/^\d{6}$/, 'Enter a 6-digit PIN.'),
});

export const whatsappConsentSchema = z.object({
  status: z.enum(['opted_in', 'opted_out']),
  method: z.enum(WHATSAPP_CONSENT_METHODS).optional(),
  note: z.string().trim().max(500).optional(),
});

export const whatsappTemplateSendSchema = z.object({
  templateId: z.string().min(1).max(100),
  /** Values for header and body placeholders, keyed by placeholder name ("1", "first_name"). */
  headerParams: z.record(z.string(), z.string().trim().min(1).max(1024)).default({}),
  bodyParams: z.record(z.string(), z.string().trim().min(1).max(1024)).default({}),
  /** The sender saw and accepted that Meta may charge for the message. */
  acknowledgeCharges: z.literal(true, { error: 'Confirm that Meta may charge for this message.' }),
  acknowledgeWarning: z.boolean().optional(),
});
export type WhatsAppTemplateSendInput = z.infer<typeof whatsappTemplateSendSchema>;

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Placeholder names in template text, in order of first appearance. */
export function templatePlaceholders(text: string | null | undefined): string[] {
  if (!text) return [];
  const seen: string[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) if (!seen.includes(m[1]!)) seen.push(m[1]!);
  return seen;
}

/** Fills placeholders for previews and the stored copy of a sent template. */
export function renderTemplateText(text: string | null | undefined, values: Record<string, string>): string {
  if (!text) return '';
  return text.replace(PLACEHOLDER, (all, name: string) => values[name] ?? all);
}

/** Business-scoped user ids look like "US.13491208655302741918"; wa_ids are digits. */
export function isWhatsAppBsuid(id: string): boolean {
  return /^[A-Z]{2}(\.ENT)?\.[A-Za-z0-9]+$/.test(id);
}
